import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import {
  getBibliothequeItems,
  getBibliothequeCategories,
} from "../../services/api";
import { BibliothequeItem, BibliothequeCategorie } from "../../types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVideo,
  faSearch,
  faFilter,
  faSpinner,
  faPlay,
  faExternalLinkAlt,
  faTimes,
  faLink,
  faChevronLeft,
  faChevronRight,
  faList,
} from "@fortawesome/free-solid-svg-icons";

const VideosPage: React.FC = () => {
  const { language } = useLanguage();
  const [videos, setVideos] = useState<BibliothequeItem[]>([]);
  const [categories, setCategories] = useState<BibliothequeCategorie[]>([]);
  const [filteredVideos, setFilteredVideos] = useState<BibliothequeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedVideo, setSelectedVideo] = useState<BibliothequeItem | null>(
    null
  );
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [showVideoList, setShowVideoList] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Find videos category ID
  const videosCategoryId = categories.find(
    (cat) => cat.nomarabe === "الفيديوهات" || cat.nomfrancais === "VIDÉOS"
  )?.id;

  // Get YouTube video ID from URL
  const getYouTubeId = (url: string): string | null => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Get YouTube video title from URL (optional)
  const getVideoTitle = (url: string) => {
    try {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);
      return params.get("v") || "YouTube Video";
    } catch {
      return "YouTube Video";
    }
  };

  useEffect(() => {
    fetchVideos();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (videosCategoryId) {
      filterVideos();
    }
  }, [videos, searchTerm, selectedCategory, videosCategoryId]);

  // Reset current video index when selected video changes
  useEffect(() => {
    if (selectedVideo) {
      setCurrentVideoIndex(0);
    }
  }, [selectedVideo]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedVideo) return;

      if (e.key === "ArrowRight") {
        nextVideo();
      } else if (e.key === "ArrowLeft") {
        prevVideo();
      } else if (e.key === "Escape") {
        closeVideoModal();
      } else if (e.key === "l" || e.key === "L") {
        setShowVideoList(!showVideoList);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedVideo, currentVideoIndex, showVideoList]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await getBibliothequeItems();
      const allItems = response.data || [];

      // Filter only active items
      const activeItems = allItems.filter(
        (item: BibliothequeItem) => item.etat
      );
      setVideos(activeItems);
      setFilteredVideos(activeItems);
    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getBibliothequeCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const filterVideos = () => {
    let filtered = videos;

    // Filter by videos category
    if (videosCategoryId) {
      filtered = filtered.filter(
        (item) => item.categorieId === videosCategoryId
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.descriptionarabe
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          item.descriptionfrancais
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Filter by selected category
    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (item) => item.categorieId === parseInt(selectedCategory)
      );
    }

    setFilteredVideos(filtered);
  };

  const getCategoryName = (categoryId: number) => {
    const category = categories.find((c) => c.id === categoryId);
    return category
      ? language === "ar"
        ? category.nomarabe
        : category.nomfrancais
      : "";
  };

  const openVideoModal = (video: BibliothequeItem) => {
    setSelectedVideo(video);
    setCurrentVideoIndex(0);
    setShowVideoList(video.liens && video.liens.length > 1);
    document.body.style.overflow = "hidden";
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    setCurrentVideoIndex(0);
    setShowVideoList(false);
    document.body.style.overflow = "unset";
  };

  const nextVideo = () => {
    if (selectedVideo && selectedVideo.liens) {
      const nextIndex = (currentVideoIndex + 1) % selectedVideo.liens.length;
      setCurrentVideoIndex(nextIndex);
    }
  };

  const prevVideo = () => {
    if (selectedVideo && selectedVideo.liens) {
      const prevIndex =
        currentVideoIndex === 0
          ? selectedVideo.liens.length - 1
          : currentVideoIndex - 1;
      setCurrentVideoIndex(prevIndex);
    }
  };

  const goToVideo = (index: number) => {
    setCurrentVideoIndex(index);
  };

  // YouTube Embed Component with enhanced features
  const YouTubeEmbed = ({
    videoId,
    autoplay = false,
  }: {
    videoId: string;
    autoplay?: boolean;
  }) => {
    return (
      <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
        <iframe
          ref={iframeRef}
          className="absolute top-0 left-0 w-full h-full rounded-lg"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=${
            autoplay ? 1 : 0
          }&modestbranding=1&rel=0&showinfo=0&controls=1`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          allowTransparency
        />
      </div>
    );
  };

  const getCurrentVideoId = () => {
    if (!selectedVideo?.liens?.[currentVideoIndex]) return null;
    return getYouTubeId(selectedVideo.liens[currentVideoIndex]);
  };

  const getCurrentVideoUrl = () => {
    if (!selectedVideo?.liens?.[currentVideoIndex]) return "";
    return selectedVideo.liens[currentVideoIndex];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center py-16">
            <FontAwesomeIcon
              icon={faSpinner}
              className="text-mauritania-green-dark text-4xl animate-spin mb-4"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-2/3 mb-8 md:mb-0">
              <h1 className="text-4xl font-bold mb-4">
                {language === "ar" ? "مكتبة الفيديوهات" : "Vidéothèque"}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-white p-3 rounded-full">
                <FontAwesomeIcon
                  icon={faVideo}
                  className="text-red-600 text-3xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search Input */}
            <div className="relative">
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                type="text"
                className="w-full pr-10 pl-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder={
                  language === "ar"
                    ? "ابحث في الفيديوهات..."
                    : "Rechercher des vidéos..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Videos Gallery */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {filteredVideos.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg shadow">
              <FontAwesomeIcon
                icon={faVideo}
                className="text-gray-400 text-6xl mb-4"
              />
              <h3 className="text-2xl font-bold text-gray-600 mb-2">
                {language === "ar"
                  ? "لا توجد فيديوهات متاحة"
                  : "Aucune vidéo disponible"}
              </h3>
              <p className="text-gray-500">
                {language === "ar"
                  ? "لم يتم إضافة أي فيديوهات بعد"
                  : "Aucune vidéo n'a été ajoutée pour le moment"}
              </p>
            </div>
          ) : (
            <>
              {/* Results Info */}
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {language === "ar"
                      ? "مجموعة الفيديوهات"
                      : "Collection de Vidéos"}
                  </h2>
                  <p className="text-gray-600">
                    {language === "ar"
                      ? `عرض ${filteredVideos.length} من ${videos.length} فيديو`
                      : `Affichage de ${filteredVideos.length} sur ${videos.length} vidéos`}
                  </p>
                </div>
                <div className="bg-red-100 text-red-800 px-4 py-2 rounded-full font-semibold">
                  {filteredVideos.length}{" "}
                  {language === "ar" ? "فيديو" : "vidéos"}
                </div>
              </div>

              {/* Videos Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredVideos.map((video) => {
                  const firstLink = video.liens?.[0];
                  const videoId = firstLink ? getYouTubeId(firstLink) : null;

                  return (
                    <div
                      key={video.id}
                      className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                    >
                      {/* Video Preview */}
                      <div className="relative">
                        {videoId ? (
                          <>
                            <div
                              className="relative"
                              style={{ paddingBottom: "56.25%" }}
                            >
                              <img
                                src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                                alt={
                                  language === "ar"
                                    ? video.descriptionarabe
                                    : video.descriptionfrancais
                                }
                                className="absolute top-0 left-0 w-full h-full object-cover"
                              />
                              <div
                                className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center cursor-pointer"
                                onClick={() => openVideoModal(video)}
                              >
                                <div className="bg-red-600 text-white p-4 rounded-full hover:bg-red-700 transition-colors">
                                  <FontAwesomeIcon
                                    icon={faPlay}
                                    className="text-2xl"
                                  />
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="bg-gray-200 h-48 flex items-center justify-center">
                            <FontAwesomeIcon
                              icon={faVideo}
                              className="text-gray-400 text-4xl"
                            />
                          </div>
                        )}

                        {video.liens && video.liens.length > 1 && (
                          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                            {video.liens.length}{" "}
                            {language === "ar" ? "فيديو" : "vidéos"}
                          </div>
                        )}
                      </div>

                      {/* Video Info */}
                      <div className="p-6">
                        <h3 className="font-bold text-gray-800 mb-3 text-lg line-clamp-2">
                          {language === "ar"
                            ? video.descriptionarabe
                            : video.descriptionfrancais}
                        </h3>

                        <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                          <span className="flex items-center">
                            <FontAwesomeIcon icon={faVideo} className="ml-1" />
                            {getCategoryName(video.categorieId)}
                          </span>
                          <span className="text-red-600 font-medium">
                            #{video.ordre}
                          </span>
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => openVideoModal(video)}
                            className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                          >
                            <FontAwesomeIcon icon={faPlay} className="ml-2" />
                            {language === "ar"
                              ? "مشاهدة الفيديو"
                              : "Regarder la vidéo"}
                          </button>

                          {firstLink && (
                            <a
                              href={firstLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center"
                              title={
                                language === "ar"
                                  ? "فتح في يوتيوب"
                                  : "Ouvrir sur YouTube"
                              }
                            >
                              <FontAwesomeIcon icon={faExternalLinkAlt} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Video Modal with Navigation */}
      {selectedVideo &&
        selectedVideo.liens &&
        selectedVideo.liens.length > 0 && (
          <div
            className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
            onClick={closeVideoModal}
          >
            <div className="relative w-full max-w-7xl max-h-[90vh] flex flex-col lg:flex-row gap-6">
              {/* Close Button */}
              <button
                className="absolute top-4 left-4 z-20 text-white bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-70 transition-all"
                onClick={closeVideoModal}
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>

              {/* Main Video Area */}
              <div className="flex-1">
                <div
                  className="bg-black rounded-lg overflow-hidden relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Video Player */}
                  {getCurrentVideoId() && (
                    <YouTubeEmbed
                      videoId={getCurrentVideoId()!}
                      autoplay={true}
                    />
                  )}

                  {/* Video Navigation Arrows (only for multiple videos) */}
                  {selectedVideo.liens.length > 1 && (
                    <>
                      <button
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 text-white bg-black bg-opacity-50 rounded-full p-4 hover:bg-opacity-70 transition-all hidden lg:block"
                        onClick={(e) => {
                          e.stopPropagation();
                          prevVideo();
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                      </button>
                      <button
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 text-white bg-black bg-opacity-50 rounded-full p-4 hover:bg-opacity-70 transition-all hidden lg:block"
                        onClick={(e) => {
                          e.stopPropagation();
                          nextVideo();
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronRight} size="lg" />
                      </button>

                      {/* Mobile Navigation Buttons */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 lg:hidden">
                        <button
                          className="bg-black bg-opacity-50 text-white rounded-full p-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            prevVideo();
                          }}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <button
                          className="bg-black bg-opacity-50 text-white rounded-full p-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            nextVideo();
                          }}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </div>
                    </>
                  )}

                  {/* Video Counter */}
                  {selectedVideo.liens.length > 1 && (
                    <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm">
                      {currentVideoIndex + 1} / {selectedVideo.liens.length}
                    </div>
                  )}

                  {/* Toggle Video List Button */}
                  {selectedVideo.liens.length > 1 && (
                    <button
                      className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm flex items-center lg:hidden"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowVideoList(!showVideoList);
                      }}
                    >
                      <FontAwesomeIcon icon={faList} className="ml-2" />
                      {language === "ar"
                        ? "قائمة الفيديوهات"
                        : "Liste des vidéos"}
                    </button>
                  )}
                </div>

                {/* Video Details */}
                <div className="bg-white rounded-lg p-6 mt-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">
                        {language === "ar"
                          ? selectedVideo.descriptionarabe
                          : selectedVideo.descriptionfrancais}
                      </h2>
                      <div className="flex flex-wrap gap-4 mt-2">
                        <div className="flex items-center text-gray-600">
                          <FontAwesomeIcon icon={faVideo} className="ml-2" />
                          <span>
                            {getCategoryName(selectedVideo.categorieId)}
                          </span>
                        </div>
                        <div className="text-gray-600">
                          {language === "ar"
                            ? "رقم الترتيب:"
                            : "Numéro d'ordre:"}{" "}
                          <span className="font-bold">
                            {selectedVideo.ordre}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-3 mt-4 md:mt-0">
                      <a
                        href={getCurrentVideoUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <FontAwesomeIcon
                          icon={faExternalLinkAlt}
                          className="ml-2"
                        />
                        {language === "ar"
                          ? "فتح في يوتيوب"
                          : "Ouvrir sur YouTube"}
                      </a>

                      {/* Video Counter */}
                      {selectedVideo.liens.length > 1 && (
                        <div className="bg-gray-100 px-4 py-2 rounded-lg">
                          <span className="text-gray-700 font-medium">
                            {currentVideoIndex + 1} /{" "}
                            {selectedVideo.liens.length}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Current Video Info */}
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-700">
                      {language === "ar"
                        ? `الفيديو ${currentVideoIndex + 1} من ${
                            selectedVideo.liens.length
                          }`
                        : `Vidéo ${currentVideoIndex + 1} sur ${
                            selectedVideo.liens.length
                          }`}
                    </p>
                    <a
                      href={getCurrentVideoUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 break-all mt-2 inline-block"
                    >
                      {getCurrentVideoUrl()}
                    </a>
                  </div>

                  {/* Navigation Dots */}
                  {selectedVideo.liens.length > 1 && (
                    <div className="flex justify-center space-x-2 mt-6">
                      {selectedVideo.liens.map((_, index) => (
                        <button
                          key={index}
                          className={`w-3 h-3 rounded-full transition-all ${
                            index === currentVideoIndex
                              ? "bg-red-600"
                              : "bg-gray-300 hover:bg-gray-400"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            goToVideo(index);
                          }}
                          aria-label={`Go to video ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Video List Sidebar */}
              {selectedVideo.liens.length > 1 && showVideoList && (
                <div className="lg:w-1/3">
                  <div
                    className="bg-gray-900 rounded-lg p-4 h-full overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-white flex items-center">
                        <FontAwesomeIcon icon={faList} className="ml-2" />
                        {language === "ar"
                          ? "قائمة الفيديوهات"
                          : "Liste des vidéos"}
                      </h3>
                      <button
                        onClick={() => setShowVideoList(false)}
                        className="text-white hover:text-gray-300 lg:hidden"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>

                    <div className="space-y-3">
                      {selectedVideo.liens.map((link, index) => {
                        const videoId = getYouTubeId(link);
                        return (
                          <div
                            key={index}
                            className={`p-3 rounded-lg cursor-pointer transition-all ${
                              index === currentVideoIndex
                                ? "bg-red-600"
                                : "bg-gray-800 hover:bg-gray-700"
                            }`}
                            onClick={() => goToVideo(index)}
                          >
                            <div className="flex items-start">
                              {videoId && (
                                <div className="w-16 h-12 flex-shrink-0 mr-3">
                                  <img
                                    src={`https://img.youtube.com/vi/${videoId}/default.jpg`}
                                    alt=""
                                    className="w-full h-full object-cover rounded"
                                  />
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-white text-sm font-medium">
                                    {language === "ar" ? "الفيديو" : "Vidéo"}{" "}
                                    {index + 1}
                                  </span>
                                  {index === currentVideoIndex && (
                                    <FontAwesomeIcon
                                      icon={faPlay}
                                      className="text-white text-xs ml-2"
                                    />
                                  )}
                                </div>
                                <p className="text-gray-300 text-xs mt-1 truncate">
                                  {link.length > 40
                                    ? link.substring(0, 40) + "..."
                                    : link}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Navigation Controls */}
                    <div className="mt-6 pt-4 border-t border-gray-700">
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={prevVideo}
                          className="flex items-center justify-center px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          <FontAwesomeIcon
                            icon={faChevronLeft}
                            className="ml-2"
                          />
                          {language === "ar" ? "السابق" : "Précédent"}
                        </button>
                        <button
                          onClick={nextVideo}
                          className="flex items-center justify-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          {language === "ar" ? "التالي" : "Suivant"}
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className="mr-2"
                          />
                        </button>
                      </div>
                      <div className="text-center text-gray-400 text-xs mt-3">
                        {language === "ar"
                          ? "استخدم الأسهم للتنقل بين الفيديوهات"
                          : "Utilisez les flèches pour naviguer entre les vidéos"}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Toggle Video List Button for Desktop */}
              {selectedVideo.liens.length > 1 && !showVideoList && (
                <button
                  className="absolute right-4 top-4 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg text-sm flex items-center hidden lg:flex"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowVideoList(true);
                  }}
                >
                  <FontAwesomeIcon icon={faList} className="ml-2" />
                  {language === "ar"
                    ? "عرض قائمة الفيديوهات"
                    : "Afficher la liste des vidéos"}
                </button>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default VideosPage;
